import { useState, useEffect, useCallback } from 'react';
import { gamificationApi } from '../api';

/**
 * チュートリアルツアー管理用のカスタムフック
 *
 * @param {string} tourKey - Tour識別キー（例: "dashboard", "research_input"）
 * @param {Object} options - オプション設定
 * @param {boolean} options.allowSkip - スキップを許可するか（デフォルト: true）
 * @returns {Object} Tour状態と制御関数
 */
export const useTutorialTour = (tourKey, options = {}) => {
  const { allowSkip = true, autoStart = true, microKey = null, disabled = false } = options;
  
  const [tourOpen, setTourOpen] = useState(false);
  const [tourReady, setTourReady] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);
  const [tourSkipped, setTourSkipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [isMicroMode, setIsMicroMode] = useState(false);

  /**
   * Tourの完了状態をバックエンドから取得
   * アンケートが完了していない場合はツアーを表示しない
   */
  const checkTourStatus = useCallback(async () => {
    if (!tourKey || disabled) {
      setLoading(false);
      return;
    }

    try {
      // まずアンケートの完了状態を確認
      const gamificationStatus = await gamificationApi.getStatus();
      const surveyCompleted = gamificationStatus.survey_completed || false;

      // アンケートが未完了の場合はツアーを表示しない
      if (!surveyCompleted) {
        setLoading(false);
        return;
      }

      const response = await gamificationApi.getTutorialTours();
      const isCompleted = response.tours[tourKey] || false;
      const isMicroCompleted = microKey ? (response.tours[microKey] || false) : true;

      setTourCompleted(isCompleted);

      // 未完了の場合
      if (!isCompleted) {
        if (autoStart) {
          if (microKey && !isMicroCompleted) {
            setIsMicroMode(true);
          }
          setTimeout(() => {
            setTourOpen(true);
          }, 500);
        } else {
          setTourReady(true);
        }
      }
    } catch (error) {
      console.error(
        `[useTutorialTour] Failed to check tour status for ${tourKey}:`,
        error,
      );
    } finally {
      setLoading(false);
    }
  }, [tourKey, autoStart, microKey, disabled]);

  useEffect(() => {
    checkTourStatus();
  }, [checkTourStatus]);

  /**
   * Tourを完了としてマーク
   */
  const completeTour = useCallback(async () => {
    if (!tourKey) return;

    try {
      const keyToMark = isMicroMode && microKey ? microKey : tourKey;
      const requestData = {
        tour_key: keyToMark,
        completed: true,
      };

      await gamificationApi.updateTutorialTour(requestData);

      if (!isMicroMode) {
        setTourCompleted(true);
      }
      setIsMicroMode(false);
      setTourOpen(false);
      setCurrentStep(0);
    } catch (error) {
      console.error(`Failed to complete tour ${tourKey}:`, error);
    }
  }, [tourKey, isMicroMode, microKey]);

  /**
   * Tourをスキップ（あとで見る）
   * 完了としてはマークせず、一時的に閉じる
   */
  const skipTour = useCallback(() => {
    if (!allowSkip) return;
    
    setTourSkipped(true);
    setTourOpen(false);
    setCurrentStep(0);
  }, [allowSkip]);

  /**
   * Tourを再表示
   */
  const restartTour = useCallback(() => {
    setTourSkipped(false);
    setIsMicroMode(false);
    setCurrentStep(0);
    setTourOpen(true);
  }, []);

  /**
   * Tourを手動で開始（autoStart=falseの場合に使用）
   */
  const startTour = useCallback(() => {
    if (tourReady && !tourCompleted && !tourSkipped) {
      setTourOpen(true);
    }
  }, [tourReady, tourCompleted, tourSkipped]);

  /**
   * Tourを手動で閉じる
   */
  const closeTour = useCallback(() => {
    setTourOpen(false);
    setCurrentStep(0);
  }, []);

  /**
   * 現在のステップを更新
   */
  const updateCurrentStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  /**
   * 総ステップ数を設定
   */
  const updateTotalSteps = useCallback((total) => {
    setTotalSteps(total);
  }, []);

  return {
    tourOpen,
    setTourOpen,
    tourCompleted,
    tourSkipped,
    tourReady,
    completeTour,
    skipTour,
    restartTour,
    startTour,
    closeTour,
    loading,
    currentStep,
    totalSteps,
    updateCurrentStep,
    updateTotalSteps,
    allowSkip,
    isMicroMode,
  };
};

export default useTutorialTour;
